import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
const faqCategories = [{
  category: 'General Information',
  questions: [{
    q: 'What is Supradhyan Enlightenment Center?',
    a: 'Supradhyan is a non-profit spiritual center founded in 1985, dedicated to teaching authentic meditation practices and supporting individuals on their journey to inner peace and self-realization.'
  }, {
    q: 'Do I need prior meditation experience?',
    a: 'No prior experience is necessary. We offer programs for all levels, from complete beginners to advanced practitioners. Our teachers will guide you step-by-step.'
  }, {
    q: 'What meditation traditions do you teach?',
    a: 'We draw from Vedic traditions, including Vipassana, pranayama, chakra meditation, and mindfulness practices, adapted for modern practitioners while maintaining authenticity.'
  }, {
    q: 'Is this a religious organization?',
    a: 'No, we are a secular spiritual organization. While we honor ancient wisdom traditions, our teachings are universal and welcoming to people of all faiths or no faith.'
  }]
}, {
  category: 'Courses & Retreats',
  questions: [{
    q: 'How do I choose the right course?',
    a: 'Consider your experience level, available time, and goals. Beginners often start with "Awakening the Inner Light" or "Mindfulness for Modern Life." Contact us for personalized recommendations.'
  }, {
    q: 'What is included in the course fee?',
    a: 'Course fees typically include all teachings, accommodation (shared or private), three vegetarian meals daily, course materials, and access to facilities. Specific inclusions vary by program.'
  }, {
    q: 'Can I attend just for the day without staying overnight?',
    a: 'Most intensive retreats require full residential participation for the transformative experience. However, we offer day programs and weekend workshops for local participants.'
  }, {
    q: 'What is your cancellation policy?',
    a: 'Full refund if cancelled 30+ days before start date, 50% refund for 15-29 days, no refund within 14 days. We understand emergencies—contact us to discuss special circumstances.'
  }]
}, {
  category: 'Accommodation & Facilities',
  questions: [{
    q: 'What are the accommodation options?',
    a: 'We offer shared rooms (2-4 people) and limited private rooms. All accommodations are simple, clean, and conducive to meditation practice. Bedding and towels are provided.'
  }, {
    q: 'What kind of food is served?',
    a: 'We serve sattvic vegetarian meals—pure, wholesome food that supports meditation practice. Meals are prepared with organic ingredients from our farm. Special dietary needs can be accommodated with advance notice.'
  }, {
    q: 'Is the center accessible for people with disabilities?',
    a: 'Yes, we have wheelchair-accessible facilities and can accommodate various physical limitations. Please inform us of any special needs when registering so we can prepare appropriately.'
  }, {
    q: 'What should I bring?',
    a: 'Comfortable clothing for meditation and yoga, personal toiletries, journal, water bottle, and any medications. Meditation cushions and mats are provided. Avoid bringing valuables or electronic devices.'
  }]
}, {
  category: 'Practice & Schedule',
  questions: [{
    q: 'What is a typical daily schedule?',
    a: 'Days begin at 6 AM with morning meditation, followed by breakfast, teaching sessions, lunch, afternoon practice, evening discourse, dinner, and silent meditation before bed. Free time is built in for rest and integration.'
  }, {
    q: 'Is silence required?',
    a: 'Some retreats include periods of noble silence to deepen practice. Other programs allow conversation during meals and breaks. Specific requirements are outlined in each course description.'
  }, {
    q: 'Can I leave the center during the retreat?',
    a: 'We encourage full participation for maximum benefit. Leaving the center disrupts your practice and the group energy. In emergencies, arrangements can be made with staff.'
  }, {
    q: 'What if I find the practice too challenging?',
    a: "It's normal to face challenges. Our teachers provide personal guidance and modifications. You can always take breaks, and we support you in finding your appropriate edge without pushing too hard."
  }]
}, {
  category: 'Financial & Scholarships',
  questions: [{
    q: 'Do you offer scholarships or financial aid?',
    a: 'Yes, we offer partial and full scholarships based on financial need. We believe meditation should be accessible to all. Apply through our scholarship form when registering.'
  }, {
    q: 'Can I volunteer instead of paying?',
    a: 'Yes, our karma yoga program allows you to serve in exchange for teachings. Volunteers typically commit to 2-6 weeks of service. This is a beautiful way to deepen practice through selfless action.'
  }, {
    q: 'Are donations tax-deductible?',
    a: 'Yes, Supradhyan is a registered 501(c)(3) non-profit organization. All donations are tax-deductible. You will receive a receipt for your records.'
  }, {
    q: 'What payment methods do you accept?',
    a: 'We accept credit/debit cards, bank transfers, checks, and online payment platforms. Payment plans are available for longer programs—contact us to arrange.'
  }]
}];
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="bg-white rounded-xl shadow-md overflow-hidden" data-id="element-775">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-[#FAF7F2] transition-colors" data-id="element-776">
        <span className="font-serif text-lg font-bold text-[#2C3E50] pr-4" data-id="element-777">
          {question}
        </span>
        <ChevronDown className={`w-5 h-5 text-[#8B9D83] flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-778" />
      </button>
      <motion.div initial={false} animate={{
      height: isOpen ? 'auto' : 0
    }} transition={{
      duration: 0.3,
      ease: 'easeInOut'
    }} className="overflow-hidden" data-id="element-779">
        <div className="px-6 pb-5 text-[#5D6D7E] leading-relaxed" data-id="element-780">{answer}</div>
      </motion.div>
    </motion.div>;
}
export function FAQPage() {
  return <div className="min-h-screen bg-[#FFFBF5]" data-id="element-781">
      <Header data-id="element-782" />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#8B9D83]/10 via-[#FAF7F2] to-[#B8A4C9]/10" data-id="element-783">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-784">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-center max-w-3xl mx-auto" data-id="element-785">
            <h1 className="font-serif text-5xl md:text-6xl text-[#2C3E50] mb-6 leading-tight" data-id="element-786">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-[#5D6D7E] leading-relaxed" data-id="element-787">
              Find answers to common questions about our center, courses, and
              meditation practices
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="py-20 bg-white" data-id="element-788">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-789">
          {faqCategories.map((category, categoryIndex) => <div key={categoryIndex} className="mb-16 last:mb-0" data-id="element-790">
              <motion.h2 initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="font-serif text-3xl text-[#2C3E50] mb-8" data-id="element-791">
                {category.category}
              </motion.h2>
              <div className="space-y-4" data-id="element-792">
                {category.questions.map((faq, index) => <FAQItem key={index} question={faq.q} answer={faq.a} data-id="element-793" />)}
              </div>
            </div>)}
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-20 bg-[#FAF7F2]" data-id="element-794">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-795">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-796">
            <h2 className="font-serif text-4xl text-[#2C3E50] mb-6" data-id="element-797">
              Still Have Questions?
            </h2>
            <p className="text-lg text-[#5D6D7E] mb-10" data-id="element-798">
              We're here to help. Reach out to our team and we'll get back to
              you within 24 hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-799">
              <button className="bg-[#8B9D83] hover:bg-[#7A8C72] text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg" data-id="element-800">
                Contact Us
              </button>
              <button className="border-2 border-[#8B9D83] text-[#8B9D83] hover:bg-[#8B9D83] hover:text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300" data-id="element-801">
                Schedule a Call
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-802" />
    </div>;
}